// =============================================================================
// src/client/queryClient.ts
// -----------------------------------------------------------------------------
// Singleton del QueryClient (extraído de main.tsx) para que capas sin hooks
// (p. ej. src/api/media.ts) puedan invalidar caches tras un write. Módulo hoja:
// no importa nada de la app, así no crea ciclos.
// =============================================================================

import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      console.error("[QueryCache]", error instanceof Error ? error : new Error(String(error)));
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      console.error("[MutationCache]", error instanceof Error ? error : new Error(String(error)));
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Los datos se consideran "frescos" por 5 minutos
      gcTime: 1000 * 60 * 30, // Mantener en caché por 30 min (antes cacheTime)
      retry: 1, // Reintentar fallos solo 1 vez
      refetchOnWindowFocus: false, // No re-peticionar al cambiar de pestaña (opcional)
    },
  },
});

// Keep the content-health ("avisos") badge live: when a query holding
// health-relevant data refreshes (after an edit is saved), re-run the health
// aggregate so the count drops immediately instead of waiting out staleTime.
// Scoped to keys the editors actually observe — excludes KDS/orders/staff churn,
// so high-frequency service screens never trigger a health refetch.
const HEALTH_WATCH = new Set([
  "tours",
  "hotspots",
  "tour-area-hotspots",
  "venue-action-buttons",
  "venue-action-buttons-active",
]);
// Same bridge for the Overview (Inicio) queries: no mutation invalidates
// ["overview",…] directly, so piggyback on the domain caches it summarises —
// when leads/contacts/bookings refresh after an edit, mark the
// overview stale so returning to Inicio shows the new rows without F5.
const OVERVIEW_WATCH = new Set([
  "all-user-leads",
  "contacts",
  "bookings",
  "tours",
]);
let healthInvalidateTimer: ReturnType<typeof setTimeout> | null = null;
let overviewInvalidateTimer: ReturnType<typeof setTimeout> | null = null;
queryClient.getQueryCache().subscribe((event) => {
  if (event.type !== "updated" || event.action?.type !== "success") return;
  const head = String(event.query.queryKey?.[0] ?? "");
  if (HEALTH_WATCH.has(head) && !healthInvalidateTimer) {
    // coalesce a save's burst into one refetch
    healthInvalidateTimer = setTimeout(() => {
      healthInvalidateTimer = null;
      void queryClient.invalidateQueries({ queryKey: ["content-health"] });
    }, 400);
  }
  if (OVERVIEW_WATCH.has(head) && !overviewInvalidateTimer) {
    overviewInvalidateTimer = setTimeout(() => {
      overviewInvalidateTimer = null;
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
    }, 400);
  }
});
